
import React, { useState, useEffect, useRef } from 'react';
import { t } from '../tokens';
import { ActiveChord, MeasureAnnotation, Measure } from '../types';
import { useFeatureAccess } from '../contexts/SubscriptionContext';

type BuildType = 'root' | 'accidental' | 'quality' | 'alteration' | 'action';

interface ChordPaletteProps {
  isOpen: boolean;
  onBuild: (type: BuildType, symbol: string) => void;
  onUndo: () => void;
  onClose: () => void;
  activeChord: ActiveChord | null;
  activeMeasure: Measure | null;
  onAnnotate: (measureIndex: number, type: keyof MeasureAnnotation, value: any) => void;
}

const BuilderButton: React.FC<{ 
    symbol: string, 
    label?: string,
    onClick: () => void, 
    className?: string,
    disabled?: boolean,
}> = ({ symbol, label, onClick, className = '', disabled = false }) => (
    <button 
        onClick={onClick}
        disabled={disabled}
        className={`px-3 h-10 rounded-md bg-[#56463C] hover:bg-[#D4A853] text-[#D3CCBD] hover:text-[#271B13] font-mono font-semibold transition-all shadow-sm text-sm flex-shrink-0 flex items-center justify-center ${className} disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#56463C] disabled:hover:text-[#D3CCBD]`}
        aria-label={label || symbol}
    >
        {label || symbol}
    </button>
);

const PaletteSection: React.FC<{title: string, children: React.ReactNode}> = ({ title, children }) => (
    <div className="mb-3">
        <h3 className="text-xs font-bold text-[#56463C] uppercase tracking-wider mb-2 px-1">{title}</h3>
        <div className="flex flex-wrap gap-1.5">
            {children}
        </div>
    </div>
);

const ChordPalette: React.FC<ChordPaletteProps> = ({ isOpen, onBuild, onUndo, onClose, activeChord, activeMeasure, onAnnotate }) => {
  const paletteRef = useRef<HTMLDivElement>(null);
  const { canUse, upgrade } = useFeatureAccess();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [size, setSize] = useState({ width: 540, height: 420 });
    const [isDocked, setIsDocked] = useState(false); // New docking state

    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [isMobileResizing, setIsMobileResizing] = useState(false);

    const dragStartOffset = useRef({ x: 0, y: 0 });
    const resizeStart = useRef({ width: 0, height: 0, x: 0, y: 0 });
    const mobileResizeStart = useRef({ height: 0, y: 0 });
    
    // Mobile and tablet both use bottom-docked behavior, only desktop is draggable
    const isFixedBottom = isMobile || isTablet;
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!isFixedBottom) { // Only desktop gets draggable positioning
            const initialWidth = 540;
            const initialHeight = 420;
            setSize({ width: initialWidth, height: initialHeight });
            setPosition({
                x: Math.max(0, window.innerWidth / 2 - initialWidth / 2),
                y: Math.max(0, window.innerHeight - initialHeight - 80),
            });
        } else {
            // Mobile and tablet use fixed bottom positioning with appropriate height
            const maxHeight = isMobile ? window.innerHeight * 0.6 : window.innerHeight * 0.5;
            setSize(s => ({ 
                ...s, 
                height: Math.min(400, maxHeight),
                width: isMobile ? window.innerWidth : Math.min(600, window.innerWidth * 0.8)
            }));
        }
    }, [isMobile, isTablet, isFixedBottom]);

    const onHeaderPointerDown = (e: React.PointerEvent) => {
        // Prevent default behaviors immediately to avoid background scrolling
        e.preventDefault();
        e.stopPropagation();
        
        // Only desktop (not mobile or tablet) allows dragging
        if (isFixedBottom) {
            // Mobile and tablet only allow height resizing
            setIsMobileResizing(true);
            mobileResizeStart.current = { height: size.height, y: e.clientY };
            const target = e.target as HTMLElement;
            target.setPointerCapture(e.pointerId);
            if (e.pointerType === 'touch') {
                target.style.touchAction = 'none';
            }
            return;
        }
        
        // Desktop dragging logic (when docked, prevent dragging)
        if (isDocked) return;

        setIsDragging(true);
        const rect = paletteRef.current?.getBoundingClientRect();
        if (rect) {
            dragStartOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        }
        
        // Improved pointer capture for better touch handling
        const target = e.target as HTMLElement;
        target.setPointerCapture(e.pointerId);
        
        // Prevent default touch behaviors that might interfere
        if (e.pointerType === 'touch') {
            target.style.touchAction = 'none';
        }
    };

    const onResizePointerDown = (e: React.PointerEvent) => {
        if (isFixedBottom) return; // No resizing for mobile/tablet
        setIsResizing(true);
        resizeStart.current = { width: size.width, height: size.height, x: e.clientX, y: e.clientY };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    };

    useEffect(() => {
        const handlePointerMove = (e: PointerEvent) => {
            e.preventDefault(); // Prevent scrolling while dragging
            
            if (isDragging) {
                // Improved drag calculation with bounds checking
                const newX = e.clientX - dragStartOffset.current.x;
                const newY = e.clientY - dragStartOffset.current.y;
                
                // Keep palette within viewport bounds
                const maxX = window.innerWidth - 320; // minimum width visible
                const maxY = window.innerHeight - 200; // minimum height visible
                
                setPosition({
                    x: Math.max(0, Math.min(newX, maxX)),
                    y: Math.max(0, Math.min(newY, maxY))
                });
            }
            if (isResizing) {
                const dx = e.clientX - resizeStart.current.x;
                const dy = e.clientY - resizeStart.current.y;
                setSize({
                    width: Math.max(320, resizeStart.current.width + dx),
                    height: Math.max(280, resizeStart.current.height + dy),
                });
            }
            if (isMobileResizing) {
                const dy = e.clientY - mobileResizeStart.current.y;
                const newHeight = mobileResizeStart.current.height - dy;
                setSize(s => ({
                    ...s,
                    height: Math.max(200, Math.min(newHeight, window.innerHeight - 50))
                }));
            }
        };

        const handlePointerUp = (e: PointerEvent) => {
            setIsDragging(false);
            setIsResizing(false);
            setIsMobileResizing(false);
            
            // Reset touch action
            if (e.pointerType === 'touch') {
                (e.target as HTMLElement).style.touchAction = '';
            }
        };

        if (isDragging || isResizing || isMobileResizing) {
            window.addEventListener('pointermove', handlePointerMove, { passive: false });
            window.addEventListener('pointerup', handlePointerUp, { once: true });
        }

        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [isDragging, isResizing, isMobileResizing]);


    const roots = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
    const accidentals = ['♯', '♭', '♮'];
    const qualities: Record<string, string[]> = {
        'Major': ['maj', 'maj7', 'maj9', '6', 'add9', '6/9'],
        'Minor': ['m', 'm7', 'm9', 'm(maj7)', 'm6', 'm11'],
        'Dominant': ['7', '9', '13', '7sus4', 'alt'],
        'Dim/Aug': ['°', '°7', 'ø7', '+', 'aug', '7♯5'],
    };
    const suspended = ['sus2', 'sus4'];
    const alterations = ['b5', '♯5', 'b9', '♯9', '♯11', 'b13'];
    const actions = [
        {label: 'Undo', value: 'undo', symbol: '↶'}, 
        {label: 'Clear', value: 'clear'},
        {label: '%', value: '%'},
        {label: '/', value: '/'},
        {label: '(', value: '('}, 
        {label: ')', value: ')'}
    ];
    const annotations = [
      // Form section markers (grey box) — for imported charts and manual use
      {label: 'A', type: 'section', value: 'A'},
      {label: 'B', type: 'section', value: 'B'},
      {label: 'C', type: 'section', value: 'C'},
      {label: 'D', type: 'section', value: 'D'},

      // Rehearsal letters (blue box) — for rehearsal cues
      {label: '[A]', type: 'rehearsalLetter', value: 'A'},
      {label: '[B]', type: 'rehearsalLetter', value: 'B'},
      {label: '[C]', type: 'rehearsalLetter', value: 'C'},
      {label: '[D]', type: 'rehearsalLetter', value: 'D'},
      
      // Navigation symbols (placement markers)
      {label: 'Segno', type: 'segno', value: true, symbol: '𝄋'},
      {label: 'Coda', type: 'coda', value: true, symbol: '⊕'},
      {label: 'Fine', type: 'fine', value: true},
      {label: 'To Coda', type: 'toCoda', value: true, symbol: '⊕'},
      
      // Compound navigation — app auto-selects "al Fine" or "al Coda" based on chart context
      {label: 'D.C.', type: 'dalCapo', value: true},
      {label: 'D.S.', type: 'dalSegno', value: true},
      
      // Individual endings
      {label: '1.', type: 'ending', value: 1},
      {label: '2.', type: 'ending', value: 2},
      {label: '3.', type: 'ending', value: 3},
    ];
    const repeats = [
        {label: 'Start Repeat', value: 'startRepeat', symbol: '||:'},
        {label: 'End Repeat', value: 'endRepeat', symbol: ':||'},
        {label: 'Double Bar (Left)', value: 'barlineLeft', annotationValue: 'double', symbol: 'L||'},
        {label: 'Double Bar (Right)', value: 'barlineRight', annotationValue: 'double', symbol: '||R'},
        {label: 'Final Bar (Left)', value: 'barlineLeft', annotationValue: 'final', symbol: 'L|‖'},
        {label: 'Final Bar (Right)', value: 'barlineRight', annotationValue: 'final', symbol: '|‖R'},
    ];

    const handleAnnotationClick = (type: string, value: any) => {
                // Repeats/barlines are free for everyone
                const isRepeat = [
                    'startRepeat',
                    'endRepeat',
                    'doubleBarline',
                    'finalBarline',
                    'barlineLeft',
                    'barlineRight',
                ].includes(type);
        
        if (!isRepeat && !canUse('annotationsEditing')) {
            upgrade('annotationsEditing');
            return;
        }
        
        if (activeChord) {
            onAnnotate(activeChord.measureIndex, type as keyof MeasureAnnotation, value);
        }
    }

    // Helper function to check if an annotation is currently active
    const isAnnotationActive = (annotation: any, currentAnnotations: any) => {
        const { type, value } = annotation;
        
        switch (type) {
            case 'section':
            case 'rehearsalLetter':
            case 'tempo':
            case 'style':
            case 'dynamics':
            case 'customText':
                return currentAnnotations[type] === value;
            
            case 'ending':
                return currentAnnotations.ending === value;
            
            case 'segno':
            case 'coda':
            case 'dalSegno':
            case 'dalCapo':
            case 'fine':
            case 'toCoda':
            case 'startRepeat':
            case 'endRepeat':
            case 'doubleBarline':
            case 'finalBarline':
                return currentAnnotations[type] === true;
            
            case 'multipleEnding':
                return currentAnnotations.multipleEnding && 
                       JSON.stringify(currentAnnotations.multipleEnding) === JSON.stringify(value);

            case 'barlineLeft':
            case 'barlineRight':
                return currentAnnotations[type] === value;
            
            default:
                return false;
        }
    };

    const activeChordString = (activeMeasure && activeChord !== null) ? (activeMeasure.chords[activeChord.chordIndex] || '') : '';
    const hasSlash = activeChordString.includes('/') && !activeChordString.includes('6/9');
    const openParenCount = (activeChordString.match(/\(/g) || []).length;
    const closeParenCount = (activeChordString.match(/\)/g) || []).length;
    const hasUnmatchedOpen = openParenCount > closeParenCount;
    const currentAnnotations = activeMeasure?.annotations || {};
    
    const mobileBaseClasses = `fixed bottom-0 left-0 w-full rounded-t-2xl shadow-2xl transition-transform duration-300 ease-in-out`;
    const mobileTransform = isOpen ? 'translate-y-0' : 'translate-y-full';
    const tabletBaseClasses = `fixed bottom-0 left-1/2 transform -translate-x-1/2 rounded-t-2xl shadow-2xl transition-transform duration-300 ease-in-out`;
    const tabletTransform = isOpen ? 'translate-y-0 -translate-x-1/2' : 'translate-y-full -translate-x-1/2';
    const desktopClasses = isDocked ? 'fixed bottom-0 right-0 rounded-tl-xl shadow-2xl' : 'fixed rounded-xl shadow-2xl';

    const containerStyle = isFixedBottom ? {
        width: isMobile ? '100%' : '600px',
        maxWidth: isMobile ? '100%' : '600px',
        height: `${size.height}px`,
        maxHeight: '90vh',
        WebkitOverflowScrolling: 'touch' as const, // Smooth scrolling on iOS
        overscrollBehavior: 'contain' as const, // Prevent parent scrolling
    } : isDocked ? {
        width: `${size.width}px`,
        height: `${size.height}px`,
        display: isOpen ? 'flex' : 'none',
    } : {
        top: `${position.y}px`,
        left: `${position.x}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        display: isOpen ? 'flex' : 'none',
    };

    const getContainerClasses = () => {
        if (isFixedBottom) {
            if (isMobile) {
                return `${mobileBaseClasses} ${mobileTransform}`;
            } else {
                // Tablet: bottom-center docking
                return `${tabletBaseClasses} ${tabletTransform}`;
            }
        } else {
            // Desktop
            return desktopClasses;
        }
    };

  return (
    <div 
        ref={paletteRef}
        className={`z-20 flex flex-col ${getContainerClasses()}`}
        style={{ ...containerStyle, backgroundColor: t.beige, border: '1px solid rgba(86,70,60,0.25)', boxShadow: '0 8px 32px rgba(35,30,27,0.45)' }}
        aria-label="Chord Builder"
        aria-hidden={!isOpen}
        data-debug-id="Level-1:ChordPalette"
    >
        <div 
            onPointerDown={onHeaderPointerDown}
            className={`relative flex-shrink-0 select-none border-b border-[#56463C]/20 ${isFixedBottom ? 'cursor-grab active:cursor-grabbing' : isDocked ? 'cursor-default' : 'cursor-move'}`}
            style={isFixedBottom ? { touchAction: 'none', userSelect: 'none' } : {}}
        >
            {/* Grab handle */}
            <div className="flex justify-center pt-1.5">
                <div className="w-10 h-1 bg-[#56463C]/40 rounded-full"></div>
            </div>
            {/* Title row: left=dock, center=title (absolute), right=preview+close */}
            <div className="relative flex items-center justify-between py-1.5 px-2">
                {!isFixedBottom ? (
                    <button
                        onClick={() => setIsDocked(!isDocked)}
                        className="p-1 rounded text-[#56463C] hover:text-[#271B13] hover:bg-[#56463C]/10 transition-colors z-10"
                        title={isDocked ? "Undock palette" : "Dock to bottom-right"}
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        {isDocked ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                            </svg>
                        ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        )}
                    </button>
                ) : <div />}
                {/* Title — absolute so it's always perfectly centered */}
                <span className="absolute inset-x-0 text-center text-[9px] font-bold uppercase tracking-[0.2em] text-[#56463C]/60 select-none pointer-events-none">
                    Chord Palette
                </span>
                <div className="flex items-center gap-1.5 z-10">
                    <span className="text-sm font-mono text-[#D4A853] px-2 py-0.5 rounded min-w-[36px] text-center leading-tight" style={{ backgroundColor: t.darkBrown }}>
                        {activeChordString || <span style={{ color: t.medBrown }}>—</span>}
                    </span>
                    <button
                        onClick={onClose}
                        className="close-button p-1 rounded text-[#56463C] hover:text-[#271B13] hover:bg-[#56463C]/15 transition-colors"
                        aria-label="Close Chord Palette"
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
        {/* Root — sticky above scroll */}
        <div className="flex-shrink-0 px-4 pt-2 pb-2.5 border-b border-[#56463C]/20" style={{ backgroundColor: '#C2BAA9' }}>
            <h3 className="text-[9px] font-bold text-[#56463C] uppercase tracking-[0.18em] mb-1.5 px-0.5">Root</h3>
            <div className="flex flex-wrap gap-1.5">
                {roots.map(r => <BuilderButton key={r} symbol={r} onClick={() => onBuild('root', r)} />)}
            </div>
        </div>
        <div 
            className="flex-grow p-4 overflow-auto"
            style={isFixedBottom ? { touchAction: 'pan-y' } : {}}
        >
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4">
                <div className="flex flex-col">
                    <PaletteSection title="Accidental">
                        {accidentals.map(a => 
                            <BuilderButton 
                                key={a}
                                symbol={a} 
                                onClick={() => onBuild(a === '♮' ? 'action' : 'accidental', a === '♮' ? 'natural' : a)} 
                            />
                        )}
                    </PaletteSection>
                    <PaletteSection title="Repeats">
                        {repeats.map(a => 
                            <BuilderButton 
                                key={a.label} 
                                label={a.symbol || a.label} 
                                symbol={a.symbol || a.value} 
                                onClick={() => handleAnnotationClick(a.value, a.annotationValue ?? true)} 
                                className={isAnnotationActive({ type: a.value, value: a.annotationValue ?? true }, currentAnnotations) ? '!bg-[#D4A853] !text-[#271B13]' : '!text-[#D3CCBD]'}
                            />
                        )}
                    </PaletteSection>
                     <PaletteSection title="Actions">
                        {actions.map(a => 
                            <BuilderButton 
                                key={a.value} 
                                label={a.symbol || a.label} 
                                symbol={a.symbol || a.value} 
                                onClick={a.value === 'undo' ? onUndo : () => onBuild('action', a.value)}
                                disabled={
                                    (a.value === '/' && hasSlash) ||
                                    (a.value === '(' && hasUnmatchedOpen) ||
                                    (a.value === ')' && !hasUnmatchedOpen)
                                }
                            />
                        )}
                    </PaletteSection>
                </div>

                <div>
                    {Object.entries(qualities).map(([groupName, symbols]) => (
                        <PaletteSection key={groupName} title={groupName}>
                            {symbols.map(s => <BuilderButton key={s} symbol={s} onClick={() => onBuild('quality', s)} />)}
                        </PaletteSection>
                    ))}
                     <PaletteSection title="Suspended">
                         {suspended.map(s => <BuilderButton key={s} symbol={s} onClick={() => onBuild('quality', s)} />)}
                    </PaletteSection>
                    <PaletteSection title="Alterations">
                         {alterations.map(a => <BuilderButton key={a} symbol={a} onClick={() => onBuild('alteration', a)} />)}
                    </PaletteSection>
                    <PaletteSection title="Annotations">
                         {!canUse('annotationsEditing') && (
                             <div className="w-full mb-2 p-2 bg-[#C5B99A] border border-[rgba(86,70,60,0.2)] rounded-md">
                                 <div className="text-xs text-[#56463C] mb-1 font-medium">👀 View-only (Free)</div>
                                 <button 
                                     onClick={() => upgrade('annotationsEditing')}
                                     className="text-xs px-2 py-1 bg-purple-600 hover:bg-purple-500 text-white rounded font-semibold transition-colors"
                                 >
                                     Upgrade to Edit
                                 </button>
                             </div>
                         )}
                         {annotations.map(a => 
                            <BuilderButton 
                                key={`${a.type}-${a.label}`} 
                                label={`${a.symbol || ''} ${a.label}`}
                                symbol={a.label} 
                                onClick={() => handleAnnotationClick(a.type, a.value)} 
                                disabled={!canUse('annotationsEditing')}
                                className={
                                    isAnnotationActive(a, currentAnnotations) ? '!bg-[#D4A853] !text-[#271B13]' : '!text-[#D3CCBD]'
                                }
                            />
                         )}
                    </PaletteSection>
                </div>
            </div>
        </div>
        {!isMobile && (
            <div 
                onPointerDown={onResizePointerDown}
                className="resize-handle absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
            >
                 <svg className="w-full h-full text-[#56463C]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 19l14-14M12 19l7-7" />
                </svg>
            </div>
        )}
    </div>
  );
};

export default ChordPalette;
